import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronUp, ChevronDown } from 'lucide-react'
import WorkspaceToolbar from './WorkspaceToolbar.jsx'
import WorkspaceTree from './WorkspaceTree.jsx'
import WorkspaceCenter from './WorkspaceCenter.jsx'
import WorkspaceBottom from './WorkspaceBottom.jsx'
import { useSpatialStore } from '@/features/spatial-model/store.js'

const BOTTOM_HEIGHT_KEY = 'arrow:workspace:bottomHeight'
const MIN_BOTTOM_HEIGHT = 120
const TOP_PADDING = 200
const DEFAULT_BOTTOM_HEIGHT = 280

function getMaxBottomHeight() {
  if (typeof window === 'undefined') return 800
  return Math.max(MIN_BOTTOM_HEIGHT, window.innerHeight - TOP_PADDING)
}

function readStoredHeight() {
  if (typeof window === 'undefined') return 280
  const raw = window.localStorage.getItem(BOTTOM_HEIGHT_KEY)
  const n = raw ? parseInt(raw, 10) : NaN
  return Number.isFinite(n) && n >= MIN_BOTTOM_HEIGHT ? n : 280
}

export default function WorkspaceShell({ loading }) {
  useSelectionDeleteShortcut()
  const [bottomHeight, setBottomHeight] = useState(readStoredHeight)
  const draggingRef = useRef(null)

  useEffect(() => {
    window.localStorage.setItem(BOTTOM_HEIGHT_KEY, String(bottomHeight))
  }, [bottomHeight])

  const onResizeStart = useCallback(
    (e) => {
      e.preventDefault()
      const startY = e.clientY
      const startH = bottomHeight
      draggingRef.current = { startY, startH }
      document.body.style.cursor = 'row-resize'
      document.body.style.userSelect = 'none'

      const onMove = (ev) => {
        const drag = draggingRef.current
        if (!drag) return
        const delta = drag.startY - ev.clientY
        const max = getMaxBottomHeight()
        const next = Math.min(max, Math.max(MIN_BOTTOM_HEIGHT, drag.startH + delta))
        setBottomHeight(next)
      }
      const onUp = () => {
        draggingRef.current = null
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [bottomHeight],
  )

  return (
    <div
      className="workspace"
      style={{ '--shell-bottom-height': `${bottomHeight}px` }}
    >
      <WorkspaceToolbar disabled={loading} />
      <aside className="workspace__left">
        <WorkspaceTree />
      </aside>
      <section className="workspace__center">
        <WorkspaceCenter loading={loading} />
      </section>
      <section className="workspace__bottom">
        <div
          className="workspace__bottom-resizer"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize bottom panel"
          onMouseDown={onResizeStart}
          onDoubleClick={() => setBottomHeight(DEFAULT_BOTTOM_HEIGHT)}
          title="Drag to resize · double-click to reset"
        />
        <BottomToggle
          height={bottomHeight}
          onToggle={() => {
            const max = getMaxBottomHeight()
            const expanded = bottomHeight >= max - 4
            setBottomHeight(expanded ? DEFAULT_BOTTOM_HEIGHT : max)
          }}
        />
        <WorkspaceBottom />
      </section>
    </div>
  )
}

function BottomToggle({ height, onToggle }) {
  const expanded = height >= getMaxBottomHeight() - 4
  const Icon = expanded ? ChevronDown : ChevronUp
  return (
    <button
      type="button"
      className="workspace__bottom-toggle"
      onClick={onToggle}
      aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
      title={expanded ? 'Collapse panel' : 'Expand panel'}
    >
      <Icon size={16} />
    </button>
  )
}

function useSelectionDeleteShortcut() {
  const selection = useSpatialStore((s) => s.selection)
  const removeNode = useSpatialStore((s) => s.removeNode)
  const removeEdge = useSpatialStore((s) => s.removeEdge)
  const removeAnchor = useSpatialStore((s) => s.removeAnchor)
  const removeDestination = useSpatialStore((s) => s.removeDestination)
  const removeZone = useSpatialStore((s) => s.removeZone)
  const removeBeacon = useSpatialStore((s) => s.removeBeacon)

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (!selection) return
      const tag = (e.target?.tagName || '').toLowerCase()
      const editable = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable
      if (editable) return
      e.preventDefault()
      switch (selection.kind) {
        case 'node': removeNode(selection.id); break
        case 'edge': removeEdge(selection.id); break
        case 'anchor': removeAnchor(selection.id); break
        case 'destination': removeDestination(selection.id); break
        case 'zone': removeZone(selection.id); break
        case 'beacon': removeBeacon(selection.id); break
        default: break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selection, removeNode, removeEdge, removeAnchor, removeDestination, removeZone, removeBeacon])
}
